import { encodeTokenForWebSocket } from '@canvas/encoding';

import { WsSocketError } from '../network/errors';
import { registerBrowserSocketCloseHandler } from '../network/register-close-handler';

import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
} from './transport';

function createSocketUrl(ctx: TransportContext): string {
  const tracedUrl = new URL(ctx.relayDetails.url);
  tracedUrl.searchParams.set('traceId', ctx.traceId);
  if (ctx.sessionId) {
    tracedUrl.searchParams.set('sessionId', ctx.sessionId);
  }
  return tracedUrl.toString();
}

/**
 * We wait for a `close` event after an `error` event, as it contains more information
 * about the error. This timeout is a safeguard in case the `close` event doesn't arrive,
 * so we don't leave the connection hanging indefinitely.
 */
export const CLOSE_EVENT_AFTER_ERROR_TIMEOUT_MS = 500;

export class WebSocketTransport implements Transport {
  private webSocket: WebSocket | undefined;
  private disposed = false;
  private errorTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly onOpen: () => void;
  private readonly onClose: (ev: CloseEvent) => void;
  private readonly onError: (ev: Event) => void;
  private readonly onMessage: (ev: MessageEvent) => void;

  constructor(
    private readonly ctx: TransportContext,
    private readonly callbacks: TransportCallbacks
  ) {
    this.onOpen = () => {
      if (!this.disposed) {
        this.callbacks.onOpen();
      }
    };

    this.onClose = (ev: CloseEvent) => {
      if (!this.disposed) {
        const closeAfterError = this.errorTimeoutId !== null;
        this.clearErrorTimeout();

        if (closeAfterError) {
          // This `close` event was fired immediately after an `error` event, so we treat
          // it as an error.
          this.callbacks.onError(new WsSocketError(ev));
        } else {
          this.callbacks.onClose(ev.code, ev.reason);
        }
      }
    };

    this.onError = (ev: Event) => {
      if (!this.disposed) {
        this.clearErrorTimeout();

        // Although we wait for the `close` event to get more details about the error,
        // there's a possibility it might not arrive. To prevent the connection from
        // hanging indefinitely, we set a timeout to trigger an error if the `close`
        // event doesn't arrive within a reasonable time frame.
        const timeoutId = setTimeout(() => {
          if (!this.disposed && this.errorTimeoutId === timeoutId) {
            this.errorTimeoutId = null;
            this.callbacks.onError(new WsSocketError(ev));
          }
        }, CLOSE_EVENT_AFTER_ERROR_TIMEOUT_MS);
        this.errorTimeoutId = timeoutId;
      }
    };

    this.onMessage = (ev: MessageEvent) => {
      if (!this.disposed) {
        this.callbacks.onMessage(ev.data);
      }
    };
  }

  connect(): void {
    const tracedUrl = createSocketUrl(this.ctx);

    const webSocket = new WebSocket(
      tracedUrl,
      encodeTokenForWebSocket(this.ctx.relayDetails.accessToken)
    );

    webSocket.addEventListener('open', this.onOpen);
    webSocket.addEventListener('close', this.onClose);
    webSocket.addEventListener('error', this.onError);
    webSocket.addEventListener('message', this.onMessage);

    if (globalThis.document) {
      registerBrowserSocketCloseHandler(webSocket, this.ctx.traceId);
    }

    webSocket.binaryType = 'arraybuffer';
    this.webSocket = webSocket;
  }

  send(data: Uint8Array<ArrayBufferLike>): void {
    this.webSocket?.send(data);
  }

  flush() {}

  close(): void {
    this.webSocket?.close();
  }

  private clearErrorTimeout(): void {
    if (this.errorTimeoutId !== null) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.clearErrorTimeout();
    if (this.webSocket) {
      this.webSocket.removeEventListener('open', this.onOpen);
      this.webSocket.removeEventListener('close', this.onClose);
      this.webSocket.removeEventListener('error', this.onError);
      this.webSocket.removeEventListener('message', this.onMessage);
      this.webSocket.close();
      this.webSocket = undefined;
    }
  }
}

export function createWebSocketTransport(
  ctx: TransportContext,
  callbacks: TransportCallbacks
): Transport {
  const transport = new WebSocketTransport(ctx, callbacks);
  transport.connect();
  return transport;
}
